import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiCheckbox,
  MiPlaceholderArea,
  MiPopper,
} from "@miplatform/components";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@miplatform/constants";
import { Box, ClickAwayListener } from "@mui/material";

export function DropTypeSelect({
  anchorEl,
  open,
  dropTypes,
  appliedDropTypes,
  selectedDropTypes,
  selectedCategoryId,
  setSelectedDropTypes,
  handleCreateCategoryDrop,
  handleCloseDropTypeSelect,
}: DropTypeSelectProps) {
  const { classes, theme } = useStyles();

  const toggleDropType = (dropTypeId: string) => {
    setSelectedDropTypes((prev) => {
      const isChecked = prev.includes(dropTypeId);
      const uncheckDropType = prev.filter((id) => id !== dropTypeId);
      const checkDropType = [...prev, dropTypeId];
      return isChecked ? uncheckDropType : checkDropType;
    });
  };

  const handleApply = () => {
    if (selectedCategoryId) {
      const drops = selectedDropTypes.filter(
        (id) => !appliedDropTypes.includes(id)
      );
      handleCreateCategoryDrop(drops, selectedCategoryId);
      handleCloseDropTypeSelect();
    }
  };

  return (
    <MiPopper
      anchorEl={anchorEl}
      open={open}
      placement="bottom"
      sx={{
        boxShadow: theme.shadows[1],
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.common.white,
      }}
    >
      <ClickAwayListener onClickAway={handleCloseDropTypeSelect}>
        <Box>
          <Box className={classes.popperContent}>
            {dropTypes.length === 0 && (
              <MiPlaceholderArea
                text="No Drop Types"
                bgColor={theme.palette.primary.light}
              />
            )}
            {dropTypes.map((dt) => {
              const isAlreadyAdded = appliedDropTypes.includes(dt.id);
              return (
                <Box key={dt.id}>
                  <MiCheckbox
                    label={dt.name}
                    size="small"
                    value={selectedDropTypes.includes(dt.id)}
                    disabled={isAlreadyAdded}
                    handleChange={() => toggleDropType(dt.id)}
                  />
                </Box>
              );
            })}
          </Box>
          <Box className={classes.popperActions}>
            <MiButton
              title="Apply"
              variant={BUTTON_VARIANTS.CONTAINED}
              size="small"
              onClick={handleApply}
            />
            <MiButton
              title="Cancel"
              variant={BUTTON_VARIANTS.TEXT}
              size="small"
              className={classes.cancelButton}
              color={BUTTON_COLORS.BLACK}
              onClick={handleCloseDropTypeSelect}
            />
          </Box>
        </Box>
      </ClickAwayListener>
    </MiPopper>
  );
}

const useStyles = makeStyles()((theme) => ({
  popperContent: {
    padding: theme.spacing(2),
    maxHeight: 200,
    overflowY: "auto",
  },
  popperActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(1, 2),
    borderTop: ` 1px solid ${theme.palette.grey.light}`,
  },
  cancelButton: {
    textDecoration: "underline",
  },
}));

interface DropTypeSelectProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  dropTypes: Array<{ id: string; name: string }>;
  appliedDropTypes: string[];
  selectedDropTypes: string[];
  setSelectedDropTypes: React.Dispatch<React.SetStateAction<string[]>>;
  selectedCategoryId: string | null;
  handleCreateCategoryDrop: (data: string[], categoryId: string) => void;
  handleCloseDropTypeSelect: () => void;
}
